import React, { useEffect, useState } from 'react';
import { animate, motion } from 'framer-motion';
import { useDb } from '../../../hooks/useDb';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import { agendaToday, nextEvent } from '../../../utils/dbSelectors';
import { durationLabel, toMinutes } from '../../../utils/time';
import { AGENDA_A0, AGENDA_A1, agendaAngleAt, agendaLabels, agendaRadius } from '../../../utils/agendaGeometry';
interface AgendaArcProps {
  cx: number;
  cy: number;
  /** Anna's visible radius */
  R: number;
}
const A0 = AGENDA_A0;
const A1 = AGENDA_A1;
const EASE = [0.23, 1, 0.32, 1] as const;
const angleAt = agendaAngleAt;

/** Today as an arc around Anna: 07:00 left, 19:00 right, a "now" marker travelling over it. */
export function AgendaArc({
  cx,
  cy,
  R
}: AgendaArcProps) {
  const reduce = useMotionPreference();
  const {
    events,
    now,
    next
  } = useDb(t => ({
    events: agendaToday(t),
    now: toMinutes(t.meta.time),
    next: nextEvent(t)
  }));
  const ra = agendaRadius(R);
  const labels = agendaLabels(events, R, next?.id);
  const nowA = angleAt(now);
  const [a, setA] = useState(nowA);
  useEffect(() => {
    if (reduce) {
      setA(nowA);
      return;
    }
    const c = animate(a, nowA, {
      duration: 0.3,
      ease: EASE,
      onUpdate: setA
    });
    return () => c.stop();
  }, [nowA]); // eslint-disable-line react-hooks/exhaustive-deps

  const pt = (ang: number, r = ra) => ({
    x: cx + r * Math.cos(ang),
    y: cy + r * Math.sin(ang)
  });
  const arc = (a0: number, a1: number, r = ra) => {
    const p0 = pt(a0, r);
    const p1 = pt(a1, r);
    return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${p1.x} ${p1.y}`;
  };
  const marker = pt(a);
  const ticks = [8, 10, 12, 14, 16, 18].map(hr => pt(angleAt(hr * 60)));
  return <motion.div className="pointer-events-none absolute inset-0 z-[2]" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.25
    }
  }} transition={{
    duration: 0.2
  }} data-id="element-1628" data-mp="2hfj95ut5vwzo">
      <ol className="sr-only" aria-label="Vandaag" data-id="element-1629" data-mp="22bq6ggot98ta">
        {events.map(e => <li key={e.id} data-id="element-1630" data-mp="1ybpjg7nnbpam">
            {e.start} {e.title}
            {e.id === next?.id ? `, over ${durationLabel(toMinutes(e.start) - now)}` : ''}
          </li>)}
      </ol>

      <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden data-id="element-1631" data-mp="1olmoi1wys19n">
        <motion.path d={arc(A0, A1)} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth={2} strokeLinecap="round" initial={{
        pathLength: reduce ? 1 : 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 0.8,
        ease: [0.65, 0, 0.35, 1]
      }} data-id="element-1632" data-mp="1jm9rwpgajftv" />
        {a > A0 + 0.01 && <path d={arc(A0, a)} fill="none" strokeWidth={2.5} strokeLinecap="round" style={{
        stroke: 'rgb(var(--accent) / 0.55)'
      }} data-id="element-1633" data-mp="15o22d2ll3gsq" />}
        {ticks.map((t, i) => <circle key={i} cx={t.x} cy={t.y} r={2.5} fill="rgba(255,255,255,0.35)" data-id="element-1634" data-mp="300jhfmw9rama" data-mpi={i} />)}
        {events.map((e, i) => {
        const isNext = e.id === next?.id;
        const stroke = e.kind === 'meeting' ? 'rgb(var(--accent))' : e.kind === 'focus' ? 'rgba(255,255,255,0.22)' : 'rgba(238,242,246,0.7)';
        return <motion.path key={e.id} d={arc(angleAt(toMinutes(e.start)), Math.max(angleAt(toMinutes(e.start)) + 0.02, angleAt(toMinutes(e.end))))} fill="none" strokeWidth={isNext ? 11 : 8} strokeLinecap="round" style={{
          stroke
        }} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2,
          delay: reduce ? 0 : 0.3 + i * 0.05
        }} data-id="element-1635" data-mp="3m85pwlz5t8a2" data-mpi={i} />;
      })}
        <circle cx={marker.x} cy={marker.y} r={16} fill="none" strokeWidth={2} style={{
        stroke: 'rgb(var(--accent) / 0.5)'
      }} data-id="element-1636" data-mp="dt56egdzj7xy" />
        <circle cx={marker.x} cy={marker.y} r={8} fill="#FFFFFF" data-id="element-1637" data-mp="ht5smn7bsnfk" />
      </svg>

      {labels.map((l, i) => {
      const e = events.find(x => x.id === l.id);
      if (!e) return null;
      return <motion.div key={l.id} className={`absolute whitespace-nowrap leading-[30px] ${l.alignRight ? 'text-right' : ''}`} style={{
        left: cx + l.x,
        top: cy + l.y,
        transform: `translate(${l.tx}, ${l.ty})`
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : 0.45 + i * 0.06
      }} data-id="element-1638" data-mp="bfi45v1aq4e8" data-mpi={i}>
            <p className={`text-[22px] ${l.isNext ? 'text-ink' : 'text-ink-soft'}`} data-id="element-1639" data-mp="ucjd1qxz0ong">
              <span className="font-semibold tabular-nums" data-id="element-1640" data-mp="19162wml84ysx">{e.start}</span> {e.title.split(',')[0]}
            </p>
            {l.isNext && <p className="text-[22px] text-accent" data-id="element-1641" data-mp="144ytukxj776r">over {durationLabel(toMinutes(e.start) - now)}</p>}
          </motion.div>;
    })}
    </motion.div>;
}